import { inject, Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { DateTime } from "luxon";
import { map } from "rxjs/operators";
import {
  GlobalStats,
  GlobalStatsAdapter,
} from "src/app/core/models/global-stats.model";
import { User } from "src/app/core/models/user.model";
import { HydraoApiService } from "src/app/core/services/core/hydrao-api.service";
import { UtilsService } from "src/app/shared/services/utils.service";
import { GraphGrouping } from "src/app/shared/types/hydrao-types";
import {
  MeterSignalAdapter,
  MeterSignalsMap,
} from "../models/meter-signal.model";
import { StatsMap } from "../models/placestats.model";
import {
  ShowerheadSignalAdapter,
  ShowerheadSignalsMap,
} from "../models/showerhead-signal.model";

@Injectable({
  providedIn: "root",
})
export class GlobalStatsService extends HydraoApiService<GlobalStats> {
  private _globalStatsAdapter: GlobalStatsAdapter = inject(GlobalStatsAdapter);
  private _meterSignalAdapter: MeterSignalAdapter = inject(MeterSignalAdapter);
  private _shSignalAdapter: ShowerheadSignalAdapter = inject(
    ShowerheadSignalAdapter
  );
  private _utils: UtilsService = inject(UtilsService);

  public getMeterStatsForStore(
    fromDate: DateTime,
    toDate: DateTime,
    grouping: GraphGrouping,
    meterUuid: string = null,
    limit: number = null,
    offset: number = null
  ): Observable<MeterSignalsMap> {
    let url = "/user-stats/meters";

    url += "?grouping=" + grouping;
    url += "&timezone=" + encodeURIComponent(this._utils.getTimezone());
    if (fromDate) {
      url += "&fromdate=" + encodeURIComponent(fromDate.toISO());
    }
    if (toDate) {
      url += "&todate=" + encodeURIComponent(toDate.toISO());
    }
    if (meterUuid) {
      url += "&uuid=" + encodeURIComponent(meterUuid);
    } else if (limit !== null && offset !== null) {
      url += `&limit=${limit}&offset=${offset}`;
    }

    return this.get(url, null, true, true).pipe(
      map((response) =>
        Object.keys(response).reduce((acc, key) => {
          acc[key] = response[key].map((item) =>
            this._meterSignalAdapter.adapt(item)
          );
          return acc;
        }, {} as MeterSignalsMap)
      )
    ) as Observable<MeterSignalsMap>;
  }

  public getShowerheadStatsForStore(
    fromDate: DateTime,
    toDate: DateTime,
    grouping: GraphGrouping,
    limit: number = null,
    offset: number = null
  ): Observable<ShowerheadSignalsMap> {
    let url = "/user-stats/v2/showerheads";

    url += "?grouping=" + grouping;
    url += "&timezone=" + encodeURIComponent(this._utils.getTimezone());
    if (fromDate) {
      url += "&fromdate=" + encodeURIComponent(fromDate.toISO());
    }
    if (toDate) {
      url += "&todate=" + encodeURIComponent(toDate.toISO());
    }
    if (limit !== null && offset !== null) {
      url += `&limit=${limit}&offset=${offset}`;
    }

    return this.get(url, null, true, true).pipe(
      map((response) =>
        Object.keys(response).reduce((acc, key) => {
          acc[key] = response[key].map((item) =>
            this._shSignalAdapter.adapt(item)
          );
          return acc;
        }, {} as ShowerheadSignalsMap)
      )
    ) as Observable<ShowerheadSignalsMap>;
  }

  public getStatsForDashboardForStore(user: User): Observable<GlobalStats> {
    let url = `/user-stats/dashboard`;

    return this.get(url, this._globalStatsAdapter, false, true);
  }

  public getMetersStatsForStore(
    fromDate: DateTime,
    toDate: DateTime
  ): Observable<StatsMap> {
    let url = "/user-stats/meters";

    let urlParams = [];
    if (fromDate) {
      urlParams.push("fromdate=" + fromDate.toJSDate().toISOString());
    }
    if (toDate) {
      urlParams.push("todate=" + toDate.toJSDate().toISOString());
    }

    if (urlParams.length > 0) {
      url += "?" + urlParams.join("&");
    }

    return this.get(url, null, true, true) as Observable<StatsMap>;
  }
}
